/**
 * api/message.ts —— 消息能力（跨会话）
 *
 * 与 session.* 的分工：
 *   session.messages('王二')  → 读**某个**会话的消息
 *   message.search('合同')     → 在**全部**会话里检索
 *
 * 用户说「谁提过合同」时是全局检索，说「王二跟我说了什么」时是会话内读 ——
 * 这两件事对用户是不同的，所以在 IL 里也分成两个对象，而不是塞一个方法加开关。
 */

import { messagesRepo } from '@/data/repo.messages';
import { sessionsRepo } from '@/data/repo.sessions';
import { contactsRepo } from '@/data/repo.contacts';
import type { ObjectDef } from '../registry';

export const messageApi = {
  name: 'message',
  desc: '在全部聊天记录里检索与统计',
  userDesc: '在全部聊天记录里搜索关键词、一键全部已读',
  methods: [
    {
      name: 'search',
      kind: 'query',
      desc: '在所有会话的聊天记录里搜索关键词。用户问「谁提过 xxx」「聊天里有没有说过 xxx」时用这个。',
      params: [{ name: 'keyword', type: 'string' as const, desc: '搜索词', required: true }],
      successText: (args: unknown[]) => `已在全部聊天记录里搜索「${String(args[0])}」`,
      run: async (args: unknown[]) => {
        const keyword = String(args[0]);
        const hits = await messagesRepo.search(keyword);
        // 附上"这条消息属于谁"，否则模型无法转述
        const views: Record<string, unknown>[] = [];
        for (const m of hits.slice(0, 50)) {
          const s = await sessionsRepo.get(m.sessionId);
          const c = s ? await contactsRepo.get(s.contactId) : undefined;
          views.push({
            from: m.sender === 'me' ? '我' : (c?.name ?? '未知'),
            with: c?.name ?? '未知',
            text: m.text,
            at: m.createdAt,
          });
        }
        return { value: views };
      },
    },
    {
      name: 'markAllRead',
      desc: '把全部消息标为已读。',
      params: [],
      successText: '已把全部消息标为已读',
      run: async () => ({ value: { count: await messagesRepo.markAllRead() } }),
    },
  ],
} satisfies ObjectDef;
